import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { useSiteContent, useLang } from "@/context/SiteContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const content = useSiteContent();
  const { lang } = useLang();
  const c = content?.contact;
  const defaultSubject = lang === "zh" ? "一般咨询" : "General Inquiry";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: defaultSubject, message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(c?.form_required_error || "Please fill in your name, email and message.", { id: "contact-error" });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.status === "success") {
        toast.success(c?.form_success || res.data.message || "Thanks!", { id: "contact-success" });
        setForm({ name: "", email: "", phone: "", subject: lang === "zh" ? "一般咨询" : "General Inquiry", message: "" });
      } else {
        toast.error(c?.form_error || "Submission failed.", { id: "contact-error" });
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || c?.form_error || "Submission failed.", { id: "contact-error" });
    } finally {
      setLoading(false);
    }
  };

  if (!c) return null;

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-36 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-orange-600" />
            <span className="text-xs tracking-[0.25em] uppercase text-slate-500 font-display">
              {content?.sections?.contact_label || "Get in Touch"}
            </span>
          </div>
          <h2 className="font-serif-sc text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
            {c.heading_top}<br />
            <span className="text-orange-600">{c.heading_bottom}</span>。
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed max-w-md">{c.description}</p>

          <div className="mt-10 space-y-5">
            <ContactRow icon={Mail} label="Email" value={c.email} />
            <ContactRow icon={Phone} label="Phone" value={c.phone} />
            <ContactRow icon={MapPin} label="Address" value={c.address} />
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white p-8 lg:p-12 border border-slate-200 rounded-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Field label={c.form_name || "Name *"}>
              <Input data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder={c.form_name || "Name"} className="editorial-input h-12 text-base" />
            </Field>
            <Field label={c.form_email || "Email *"}>
              <Input data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="editorial-input h-12 text-base" />
            </Field>
            <Field label={c.form_phone || "Phone"}>
              <Input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="+60 12-345 6789" className="editorial-input h-12 text-base" />
            </Field>
            <Field label={c.form_subject || "Subject"}>
              <Input data-testid="contact-input-subject" value={form.subject} onChange={update("subject")} placeholder={defaultSubject} className="editorial-input h-12 text-base" />
            </Field>
          </div>

          <div className="mt-8">
            <Field label={c.form_message || "Message *"}>
              <Textarea data-testid="contact-input-message" value={form.message} onChange={update("message")} placeholder="..." rows={5} className="editorial-input text-base resize-none" />
            </Field>
          </div>

          <div className="mt-10 flex items-center justify-between gap-4">
            <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
              {c.form_disclaimer}
            </p>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-7 py-6 text-sm font-medium tracking-wide active:scale-95 transition-all disabled:opacity-60"
            >
              {loading ? (c.form_sending || "Sending...") : (c.form_submit || "Send")}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display">{label}</div>
        <div className="text-slate-900 mt-1 font-medium">{value}</div>
      </div>
    </div>
  );
}
